import { FormikProps } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "types";
import * as Yup from "yup";

import { ITestable } from "@/browser";
import { IConsultantProfile, IConsultantProfileSkill } from "@/data/consultant-profiles";
import { ISkill } from "@/data/skills";
import { Flex, FloatedList, Group, IChangeable, Section } from "@/eworkitComponents";
import { IWithFormikFieldProps, withFormikField } from "@/formikComponents";
import { SuggestedSkillsSection } from "@/views/components/ConsultantProfile/Skills";
import { SkillRemove } from "@/views/components/Forms/Skills/components";
import { SkillCreatableTypeahead } from "@/views/components/SkillCreatableTypehead";

import { ISkillsFormSectionValues } from "./SkillsForm";

export const validationSkillsSectionSchema = Yup.object().shape({
  skills: Yup.array().of(Yup.object()),
});

interface ISkillsSectionProps extends IWithFormikFieldProps, IChangeable, ITestable {
  value: IConsultantProfileSkill[];
  profile: IConsultantProfile;
  setFieldTouched: FormikProps<ISkillsFormSectionValues>["setFieldTouched"];
}

export const SkillsSection: React.FC<ISkillsSectionProps> = ({
  onChange,
  value,
  testName,
  profile,
  setFieldTouched,
}) => {
  const { t } = useTranslation("session");

  const cvIds = useMemo(() => profile.cvs.map(cv => cv.id), [profile]);

  const isPrivateProfile = !profile.companyRole;

  const handleAdd = () => (skill: ISkill | null) => {
    if (!skill) {
      return;
    }

    if (setFieldTouched) {
      setFieldTouched("skills", true);
    }

    if (onChange) {
      onChange([...value, { skill }]);
    }
  };

  const handleRemove = () => (skill: ISkill) => {
    const skills = value.filter(v => v.skill.id !== skill.id);

    if (setFieldTouched) {
      setFieldTouched("skills", true);
    }

    if (onChange) {
      onChange(skills);
    }
  };

  const filterOption = (option: Option<ISkill>) => !value.find(v => v.skill.id === option.data.id);

  const renderBadge = (badgeValue: IConsultantProfileSkill) => {
    return (
      <Flex align="center" key={`badge-${badgeValue.skill.id}`}>
        <SkillRemove
          color="secondary"
          size="md"
          onClick={() => handleRemove()(badgeValue.skill)}
          text={badgeValue.skill.name}
          testName={`skill[${badgeValue.skill.id}]`}
        />
      </Flex>
    );
  };

  return (
    <Section direction="column" innerMargin="md" testName={testName}>
      <Flex>
        <Group
          label={t("CONSULTANT_PROFILES.DETAIL.SKILLS.SEARCH")}
          direction="column"
          required={isPrivateProfile}
          width={{ xs: 12, md: 5 }}
          grow={1}>
          <SkillCreatableTypeahead
            name="skills"
            labelKey="name"
            placeholder={t("CONSULTANT_PROFILES.DETAIL.SKILLS.PLACEHOLDER")}
            valueKey="id"
            onChange={handleAdd()}
            filterOption={filterOption}
            testName="skills"
          />
        </Group>
      </Flex>
      {!!value.length && <FloatedList size="sm">{value.map(renderBadge)}</FloatedList>}

      <SuggestedSkillsSection
        selectedSkills={value}
        onSkillClick={handleAdd()}
        onCheckedSkillClick={handleRemove()}
        roleId={profile.roles[0]?.role.id}
        cvIds={cvIds}
      />
    </Section>
  );
};

export const FormikSkillsSection = withFormikField(SkillsSection);
